import "../public/assets/css/index.css";
import "bootstrap/scss/bootstrap.scss";
import "@vahak/core/dist/scss/index.scss";
import "styles/aboutus.scss";
import "styles/MostSearchedLinks.scss";
import "styles/DashboardNav.scss";
import "styles/Search.scss";
import "styles/moengage-optin.scss";

import { appWithTranslation } from "next-i18next";
import { CookiesProvider } from "react-cookie";
import React, { useEffect, useLayoutEffect } from "react";
import { useRouter } from "next/router";
import type { ReactElement, ReactNode } from "react";
import type { NextPage } from "next";
import type { AppProps } from "next/app";
import { ToastContainer } from "react-toastify";
import AppContextWrapper from "AppContextWrapper";
import { QueryClientProvider } from "@tanstack/react-query";
import dynamic from "next/dynamic";
import Script from "next/script";
import Head from "next/head";
import { getlayoutForPage } from "utils/getlayoutForPage";
import { ROUTES } from "@vahak/core/dist/constants/routeStrings";
import { COOKIES, FB_PIXEL_ID, PRODUCTION_ENV } from "@vahak/core/dist/constants/misc";
import RouteTransitionLoader from "../components/common/route-transition-loader/RouteTransitionLoader";
import AuthVerifyComponent from "./AuthVerifyComponent";
import GlobalComponentsWrapper from "../components/global-components-wrapper";
import { BiddingFormProvider } from "../BiddingFormContextProvider";
import { MarketplaceProvider } from "../MarketplaceContextProvider";
import zIndexList from "@vahak/core-ui/dist/constants/zIndexList";
import { Cookies } from "react-cookie";
import { PaymentProvider } from "../context/payment-context";
import { LoadLorryPostingProvider } from "../context/load-lorry-posting";
import CommonGlobalComponentsWrapper from "../components/common-global-wrapper";
import CompactLoadPostingAppProvider from "../components/homepage-v2/compact-load-posting/CompactLoadPostingAppProvider";
import { MidnightVerificationProvider } from "../components/homepage-v2/midnight-load-verification";
import CompactLoadBiddingAppProvider from "../components/homepage-v2/compact-load-bidding/CompactLoadBiddingAppProvider";
import { createVahakQueryClient } from "@vahak/core/dist/hooks/useVahakQueryClient";
import { DetailsPageContextProvider } from "../DetailsPageContextProvider";
import { CommonProviderWrapper } from "../context/CommonProviderWrapper";
import { OrganizationSchemaScript } from "../components/seo-header/HomePageMetaScript";
import { GlobalSeoHead, SeoProvider } from "../components/seo-header/PageSeo";

type NextPageWithLayout = NextPage<{ isMobile: boolean }> & {
    getLayout?: (page: ReactElement) => ReactNode;
};

type AppPropsWithLayout = AppProps<{ isMobile: boolean }> & {
    Component: NextPageWithLayout;
};
const queryClient = createVahakQueryClient();

const IS_PROD_ENV = process.env.NEXT_PUBLIC_REACT_APP_ENV === PRODUCTION_ENV;

const ReactQueryDevtools =
    process.env.NEXT_PUBLIC_REACT_APP_ENV === "production"
        ? () => null
        : dynamic(
              () => import("@tanstack/react-query-devtools").then((mod) => mod.ReactQueryDevtools),
              { ssr: false }
          );

const gaId = IS_PROD_ENV ? process.env.NEXT_PUBLIC_REACT_APP_GA_ID_PROD : process.env.NEXT_PUBLIC_REACT_APP_GA_ID_TEST;

const moengageAppId = IS_PROD_ENV
    ? process.env.NEXT_PUBLIC_REACT_APP_MOENGAGE_PROD
    : process.env.NEXT_PUBLIC_REACT_APP_MOENGAGE_TEST;

const moEngageDebugLogs = !IS_PROD_ENV && process.env.NEXT_PUBLIC_REACT_APP_MOENGAGE_DEBUGGER_MODE !== "false" ? 1 : 0;

const moeDataCenter = "dc_3";

function MyApp({ Component, pageProps }: AppPropsWithLayout) {
    const router = useRouter();
    const cookies = new Cookies();
    const vahakAutomationCookie = cookies.get(COOKIES.vahakAutomation);
    let isVahakAutomation = false;
    if (vahakAutomationCookie) {
        isVahakAutomation = Boolean(JSON.parse(vahakAutomationCookie));
    }
    // Use the layout defined at the page level, if available
    const getLayout = getlayoutForPage({ Component, pageProps });

    const useIsomorphicLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;

    useIsomorphicLayoutEffect(() => {
        document.documentElement.lang = router.locale || "en";
    }, [router.locale]);

    /**
     * use this to set support for webP
     */
    useIsomorphicLayoutEffect(() => {
        function _isWebPSupported() {
            return new Promise<boolean>((res) => {
                const webP: any = new Image();
                webP.src =
                    "data:image/webp;base64,UklGRjoAAABXRUJQVlA4IC4AAACyAgCdASoCAAIALmk0mk0iIiIiIgBoSygABc6WWgAA/veff/0PP8bA//LwYAAA";
                webP.onload = webP.onerror = () => {
                    res(webP.height === 2);
                };
            });
        }
        _isWebPSupported().then((res) => {
            process.env.isWebPSupported = res ? "1" : "0";
            // localStorage.setItem("isWebPSupported", res ? "1" : "0");
        });
    }, []);

    useEffect(() => {
        if ("serviceWorker" in navigator) {
            window.addEventListener("load", function () {
                navigator.serviceWorker.register("/serviceworker.js").then(
                    function (registration) {
                        console.log("Service Worker registration successful with scope: ", registration.scope);
                    },
                    function (err) {
                        console.log("Service Worker registration failed: ", err);
                    }
                );
            });
        }
    }, []);

    const isMobile = pageProps.isMobile ?? false;
    const hideChatMenuInExcludedRoutes = [ROUTES.membershipPayment.getUrl()].includes(router.asPath.split("?")[0]);

    // useEffect(() => {
    //     const TAWK = (window as any)["Tawk_API"];
    //     if (TAWK && hideChatMenuInExcludedRoutes) {
    //         TAWK?.hideWidget();
    //     }
    //     return () => {
    //         if (TAWK) {
    //             TAWK?.showWidget();
    //         }
    //     };
    // }, [router.asPath]);

    return (
        <SeoProvider>
            <>
                {/* Google Tag Manager (noscript) */}
                <Head>
                    <meta
                        name="viewport"
                        content="width=device-width,minimum-scale=1,initial-scale=1,interactive-widget=resizes-content"
                    />
                    <noscript>
                        <iframe
                            src="https://www.googletagmanager.com/ns.html?id=GTM-PKTN2X8"
                            height="0"
                            width="0"
                            style={{ display: "none", visibility: "hidden" }}
                        ></iframe>
                    </noscript>
                    <link rel="preconnect" href="https://dev.visualwebsiteoptimizer.com" />
                    <OrganizationSchemaScript />
                </Head>

                <Script
                    id="vwoCode"
                    strategy="lazyOnload"
                    dangerouslySetInnerHTML={{
                        __html: `
                        window._vwo_code || (function() {
                        var account_id=684830,
                        version=2.0,
                        settings_tolerance=2000,
                        hide_element='body',
                        hide_element_style = 'opacity:0 !important;filter:alpha(opacity=0) !important;background:none !important',
                        f=false,w=window,d=document,v=d.querySelector('#vwoCode'),cK='_vwo_'+account_id+'_settings',cc={};try{var c=JSON.parse(localStorage.getItem('_vwo_'+account_id+'_config'));cc=c&&typeof c==='object'?c:{}}catch(e){}var stT=cc.stT==='session'?w.sessionStorage:w.localStorage;code={use_existing_jquery:function(){return typeof use_existing_jquery!=='undefined'?use_existing_jquery:undefined},library_tolerance:function(){return typeof library_tolerance!=='undefined'?library_tolerance:undefined},settings_tolerance:function(){return cc.sT||settings_tolerance},hide_element_style:function(){return'{'+(cc.hES||hide_element_style)+'}'},hide_element:function(){return typeof cc.hE==='string'?cc.hE:hide_element},getVersion:function(){return version},finish:function(){if(!f){f=true;var e=d.getElementById('_vis_opt_path_hides');if(e)e.parentNode.removeChild(e)}},finished:function(){return f},load:function(e){var t=this.getSettings(),n=d.createElement('script'),i=this;if(t){n.textContent=t;d.getElementsByTagName('head')[0].appendChild(n);if(!w.VWO||VWO.caE){stT.removeItem(cK);i.load(e)}}else{n.fetchPriority='high';n.src=e;n.type='text/javascript';n.onerror=function(){w._vwo_code.finish()};d.getElementsByTagName('head')[0].appendChild(n)}},getSettings:function(){try{var e=stT.getItem(cK);if(!e){return}e=JSON.parse(e);if(Date.now()>e.e){stT.removeItem(cK);return}return e.s}catch(e){return}},init:function(){if(d.URL.indexOf('__vwo_disable__')>-1)return;var e=this.settings_tolerance();w._vwo_settings_timer=setTimeout(function(){w._vwo_code.finish();stT.removeItem(cK)},e);var t=d.currentScript,n=d.createElement('style'),i=this.hide_element(),r=t&&!t.async&&i?i+this.hide_element_style():'',c=d.getElementsByTagName('head')[0];n.setAttribute('id','_vis_opt_path_hides');v&&n.setAttribute('nonce',v.nonce);n.setAttribute('type','text/css');if(n.styleSheet)n.styleSheet.cssText=r;else n.appendChild(d.createTextNode(r));c.appendChild(n);this.load('https://dev.visualwebsiteoptimizer.com/j.php?a='+account_id+'&u='+encodeURIComponent(d.URL)+'&vn='+version)}};w._vwo_code=code;code.init();})();(function(){var i=window;function t(){if(i._vwo_code){var e=t.hidingStyle=document.getElementById('_vis_opt_path_hides')||t.hidingStyle;if(!i._vwo_code.finished()&&!_vwo_code.libExecuted&&(!i.VWO||!VWO.dNR)){if(!document.getElementById('_vis_opt_path_hides')){document.getElementsByTagName('head')[0].appendChild(e)}requestAnimationFrame(t)}}}t()})();`
                    }}
                />

                {/* <Script
                id="wv-script"
                strategy="afterInteractive"
                dangerouslySetInnerHTML={{
                    __html: `
                        function sT(params) {
                            window.wvp = params;
                        }`
                }}
            /> */}

                <Script
                    id="my-script-2"
                    strategy="afterInteractive"
                    dangerouslySetInnerHTML={{
                        __html: `window.vahak = {}; window.vahak.is_p = "${IS_PROD_ENV}";`
                    }}
                />

                {/* <Script
                id="my-script-1"
                strategy="lazyOnload"
                dangerouslySetInnerHTML={{
                    __html: `
                    (function (w, d, s, l, i) {
                        w[l] = w[l] || [];
                        w[l].push({"gtm.start": new Date().getTime(),
                        event: "gtm.js"});var f = d.getElementsByTagName(s)[0],
                        j = d.createElement(s),dl = l != "dataLayer" ? "&l=" + l : "";
                        j.src = "https://www.googletagmanager.com/gtm.js?id=" + i + dl;
                        f.parentNode.insertBefore(j, f);
                    })(window, document, "script", "dataLayer", "GTM-PKTN2X8");`
                }}
            /> */}

                {/* Global site tag (gtag.js) - Google Analytics */}
                <Script strategy="lazyOnload" src={`https://www.googletagmanager.com/gtag/js?id="${gaId}"`} />
                <Script
                    id="my-script-4"
                    strategy="lazyOnload"
                    dangerouslySetInnerHTML={{
                        __html: `
                    window.dataLayer = window.dataLayer || [];
                    function gtag() {
                        dataLayer.push(arguments);
                    };
                    gtag("js", new Date());
                    function getUserId(){
                        if(document.cookie){
                            if(document.cookie.split("; ")){
                                for(let c of document.cookie.split("; ")){
                                    if(c.split("=")){
                                        for(let c1 of c.split("=")){
                                            if(c1==="ui"){
                                                return c.split("=")[1]
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                    gtag("config","${gaId}", { send_page_view: false, user_id: getUserId()});
                    `
                    }}
                />

                {/* Meta Pixel */}
                <Script
                    id="meta-pixel"
                    strategy="lazyOnload"
                    dangerouslySetInnerHTML={{
                        __html: `
                    !function(f,b,e,v,n,t,s)
                    {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
                    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
                    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
                    n.queue=[];t=b.createElement(e);t.async=!0;
                    t.src=v;s=b.getElementsByTagName(e)[0];
                    s.parentNode.insertBefore(t,s)}(window, document,'script',
                    'https://connect.facebook.net/en_US/fbevents.js');
                    fbq('init', '${FB_PIXEL_ID}');
                    fbq('track', 'PageView');
                    `
                    }}
                />

                {/* MoEngage Analytics */}
                <Script
                    id="moengage-script"
                    strategy="afterInteractive"
                    dangerouslySetInnerHTML={{
                        __html: `
                    var moeDataCenter = "dc_3";
                    !(function (e, n, i, t, a, r, o, d) {
                        if (!moeDataCenter || !moeDataCenter.match(/^dc_[0-9]+$/gm))
                            return console.error(
                                "Data center has not been passed correctly. Please follow the SDK installation instruction carefully."
                            );
                        var s = (e[a] = e[a] || []);
                        if (((s.invoked = 0), s.initialised > 0 || s.invoked > 0))
                            return (
                                console.error("MoEngage Web SDK initialised multiple times. Please integrate the Web SDK only once!"), !1
                            );
                        e.moengage_object = a;
                        var l = {},
                            g = function n(i) {
                                return function () {
                                    for (var n = arguments.length, t = Array(n), a = 0; a < n; a++) t[a] = arguments[a];
                                    (e.moengage_q = e.moengage_q || []).push({ f: i, a: t });
                                };
                            },
                            u = [
                                "track_event",
                                "add_user_attribute",
                                "add_first_name",
                                "add_last_name",
                                "add_email",
                                "add_mobile",
                                "add_user_name",
                                "add_gender",
                                "add_birthday",
                                "destroy_session",
                                "add_unique_user_id",
                                "update_unique_user_id",
                                "moe_events",
                                "call_web_push",
                                "track",
                                "location_type_attribute"
                            ],
                            m = { onsite: ["getData", "registerCallback"] };
                        for (var c in u) l[u[c]] = g(u[c]);
                        for (var v in m) for (var f in m[v]) null == l[v] && (l[v] = {}), (l[v][m[v][f]] = g(v + "." + m[v][f]));
                        (r = n.createElement(i)),
                            (o = n.getElementsByTagName("head")[0]),
                            (r.async = 1),
                            (r.src = t),
                            o.appendChild(r),
                            (e.moe =
                                e.moe ||
                                function () {
                                    return ((s.invoked = s.invoked + 1), s.invoked > 1)
                                        ? (console.error(
                                            "MoEngage Web SDK initialised multiple times. Please integrate the Web SDK only once!"
                                        ),
                                        !1)
                                        : ((d = arguments.length <= 0 ? void 0 : arguments[0]), l);
                                }),
                            r.addEventListener("load", function () {
                                if (d) return (e[a] = e.moe(d)), (e[a].initialised = e[a].initialised + 1 || 1), !0;
                            }),
                            r.addEventListener("error", function () {
                                return console.error("Moengage Web SDK loading failed."), !1;
                            });
                    })(
                        window,
                        document,
                        "script",
                        "https://cdn.moengage.com/release/${moeDataCenter}/moe_webSdk.min.latest.js",
                        "Moengage"
                    );

                    Moengage = moe({
                    app_id:"${moengageAppId}",
                    debug_logs: ${moEngageDebugLogs},
                    cards: {
                        enable: true,
                        placeholder: '#cardIcon',
                        backgroundColor: "#F6FBFC", // any valid CSS color format
                        overLayColor: "rgba(0, 0, 0, 0.8)",
                        fontFaces: [{
                            family: "Sofia",
                            url: "https://fonts.gstatic.com/s/sofia/v14/8QIHdirahM3j_su5uI0Orbjl.woff2"
                        }, {
                            family: "Audiowide",
                            url: "https://fonts.gstatic.com/s/audiowide/v16/l7gdbjpo0cum0ckerWCdlg_OMRlABg.woff2"
                        }],
                        cardDismiss: {
                            color: "#db2828", // any valid CSS color format
                            enable: true // boolean value, which enable the dismiss option.
                        },
                        optionButtonColor: "#C4C4C4", // any valid CSS color format
                        dateTimeColor: "#8E8E8E", // any valid CSS color format
                        unclickedCardIndicatorColor: "blue", // any valid CSS color format
                        pinIcon: "https://app-cdn.moengage.com/sdk/pin-icon.svg", // absolute path to the icon image.
                        refreshIcon: "https://app-cdn.moengage.com/sdk/refresh-icon.svg", // absolute path to the icon image.
                        navigationBar: {
                            backgroundColor: "#00237C", // any valid CSS color format
                            text: "Notifications", // string. eg, Notifications
                            color: "#fff", // any valid CSS color format
                            fontSize: "16px", // any valid CSS size format
                            fontFamily: "", // any font family which is added to the website
                        },
                        closeButton: {
                            webIcon: "https://app-cdn.moengage.com/sdk/cross-icon.svg",
                            mWebIcon: "https://app-cdn.moengage.com/sdk/cross-icon.svg",
                        },
                        tab: {
                            active: {
                            color: "#06A6B7",
                            underlineColor: "#06A6B7",
                            backgroundColor: "transparent"
                            },
                            inactiveTabFontColor: "#7C7C7C",
                            fontSize: "14px", // any valid CSS size format
                            fontFamily: "", // any font family which is added to the website
                            backgroundColor: "#fff", // any valid CSS color format
                        },
                        // webFloating: {
                        //     enable: ${!isVahakAutomation}, // false by default
                        //     icon: "https://app-cdn.moengage.com/sdk/bell-icon.svg", // absolute path to the icon image. by default, our icon will be used.
                        //     postion: "0px 10px 80px 0", // need all 4 offset in proper CSS format in the order of top, right, bottom, left.
                        //     countBackgroundColor: "#FF5A5F",
                        //     countColor: "#fff",
                        //     iconBackgroundColor: "#D9DFED",
                        //     fontFamily: "Audiowide"
                        // },
                        // mWebFloating: {
                        //     enable: ${!isVahakAutomation}, // false by default
                        //     icon: "https://app-cdn.moengage.com/sdk/bell-icon.svg", // absolute path to the icon image. by default, our icon will be used.
                        //     postion: "0px 7px 130px 0", // need all 4 offset in proper CSS format in the order of top, right, bottom, left.
                        //     countBackgroundColor: "#FF5A5F",
                        //     countColor: "#fff",
                        //     iconBackgroundColor: "#D9DFED",
                        //     fontFamily: "Audiowide"
                        // },
                        card: {
                            headerFontSize: "16px",
                            descriptionFontSize: "14px",
                            ctaFontSize: "12px",
                            fontFamily: "inherit",
                            horizontalRowColor: "#D9DFED"
                        },
                        noDataContent: {
                            img: "https://app-cdn.moengage.com/sdk/cards-no-result.svg",
                            text: "No notifications to show, check again later.",
                        }
                    },
                    cluster: "DC_3",
                    swPath: "/serviceworker.js"
                    }); 
                    
                    `
                    }}
                />

                {/* <Script
                id="clarity-script"
                strategy="afterInteractive"
                dangerouslySetInnerHTML={{
                    __html: `
                        (function(c,l,a,r,i,t,y){
                            c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
                            t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
                            y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
                        })(window, document, "clarity", "script", "l1ll40u3es");`
                }}
            /> */}

                <CookiesProvider>
                    <QueryClientProvider client={queryClient} >
                        <ReactQueryDevtools initialIsOpen={false} />
                        <ToastContainer newestOnTop style={{ zIndex: zIndexList.ToastContainer }} />
                        <CommonProviderWrapper>
                            <AppContextWrapper isMobile={pageProps.isMobile}>
                                <MarketplaceProvider>
                                    <PaymentProvider>
                                        <BiddingFormProvider>
                                            <LoadLorryPostingProvider>
                                                <DetailsPageContextProvider>
                                                    <MidnightVerificationProvider>
                                                        <CompactLoadPostingAppProvider>
                                                            <CompactLoadBiddingAppProvider>
                                                                {getLayout(
                                                                    <Component
                                                                        {...pageProps}
                                                                        isMobile={isMobile || false}
                                                                    />
                                                                )}
                                                                <GlobalSeoHead />
                                                                <RouteTransitionLoader app="nextjs" />
                                                                {/* Any components which needs to be added globally add it inside this component */}
                                                                <GlobalComponentsWrapper pageRoute={(pageProps as { route?: string })?.route} />
                                                                <CommonGlobalComponentsWrapper />
                                                            </CompactLoadBiddingAppProvider>
                                                        </CompactLoadPostingAppProvider>
                                                    </MidnightVerificationProvider>
                                                </DetailsPageContextProvider>
                                            </LoadLorryPostingProvider>
                                        </BiddingFormProvider>
                                    </PaymentProvider>
                                    <AuthVerifyComponent />
                                </MarketplaceProvider>
                            </AppContextWrapper>
                        </CommonProviderWrapper>
                    </QueryClientProvider>
                </CookiesProvider>
            </>
        </SeoProvider>
    );
}
export default appWithTranslation(MyApp);
